import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminCreateResource, fetchResourceTypes, getAdminToken } from '../../api/client';

function slugify(text) {
  return (text || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

const BADGE_OPTIONS = [
  'verified', 'editors_pick', 'trending_tiktok', 'trending_youtube',
  'hidden_gem', 'weird_ai', 'beginner_friendly', 'rising_creator',
  'new', 'popular',
];

export default function AdminResourceCreate() {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    website_url: '',
    resource_type: 'tool',
    primary_category: '',
    subcategory: '',
    short_description: '',
    pricing_type: 'unknown',
    tags_str: '',
    badges: [],
    featured: false,
    verified: false,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) {
      nav('/admin/login');
      return;
    }
    fetchResourceTypes().then(setTypes).catch(() => setTypes([]));
  }, [nav]);

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === 'name' && !f.slug) next.slug = slugify(v);
    return next;
  });

  const toggleBadge = (b) => {
    const cur = new Set(form.badges);
    if (cur.has(b)) cur.delete(b); else cur.add(b);
    update('badges', Array.from(cur));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    const payload = {
      name: form.name.trim(),
      slug: (form.slug || slugify(form.name)).trim(),
      website_url: form.website_url.trim(),
      resource_type: form.resource_type,
      primary_category: form.primary_category.trim() || 'Uncategorized',
      subcategory: form.subcategory.trim() || null,
      short_description: form.short_description.trim() || null,
      pricing_type: form.pricing_type,
      tags: form.tags_str.split(',').map((s) => s.trim()).filter(Boolean),
      badges: form.badges,
      featured: !!form.featured,
      verified: !!form.verified,
    };
    try {
      const created = await adminCreateResource(payload);
      nav(`/admin/tools/${created.slug}/edit`);
    } catch (err) {
      setMsg('Create failed: ' + (err?.response?.data?.detail || err.message));
      setSaving(false);
    }
  };

  return (
    <div className="page-container admin-page">
      <div className="crumbs">
        <Link to="/admin/tools">Resources</Link>
        <span>/</span>
        <span>Create</span>
      </div>
      <h1 className="page-title">Create resource</h1>
      <form className="admin-edit-form" onSubmit={submit} data-testid="admin-create-form">
        <div className="field-row">
          <label className="field">
            <span>Resource type</span>
            <select value={form.resource_type} onChange={(e) => update('resource_type', e.target.value)} data-testid="create-resource-type">
              {types.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
          </label>
        </div>
        <label className="field">
          <span>Name *</span>
          <input required value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="create-name" />
        </label>
        <label className="field">
          <span>Slug (auto from name — editable)</span>
          <input value={form.slug} onChange={(e) => update('slug', e.target.value)} data-testid="create-slug" />
        </label>
        <label className="field">
          <span>Website URL *</span>
          <input type="url" required value={form.website_url} onChange={(e) => update('website_url', e.target.value)} data-testid="create-website" />
        </label>
        <div className="field-row">
          <label className="field">
            <span>Primary category *</span>
            <input required value={form.primary_category} onChange={(e) => update('primary_category', e.target.value)} data-testid="create-category" />
          </label>
          <label className="field">
            <span>Subcategory</span>
            <input value={form.subcategory} onChange={(e) => update('subcategory', e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span>Short description</span>
          <textarea rows={3} value={form.short_description} onChange={(e) => update('short_description', e.target.value)} />
        </label>
        <label className="field">
          <span>Tags (comma separated)</span>
          <input value={form.tags_str} onChange={(e) => update('tags_str', e.target.value)} />
        </label>
        <div className="field">
          <span>Badges</span>
          <div className="badge-picker">
            {BADGE_OPTIONS.map((b) => (
              <button
                type="button"
                key={b}
                className={`badge-toggle ${form.badges.includes(b) ? 'active' : ''}`}
                onClick={() => toggleBadge(b)}
              >
                {b.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
        <div className="field-row">
          <label className="field">
            <span>Pricing</span>
            <select value={form.pricing_type} onChange={(e) => update('pricing_type', e.target.value)}>
              <option value="unknown">Unknown</option>
              <option value="free">Free</option>
              <option value="freemium">Freemium</option>
              <option value="paid">Paid</option>
              <option value="open-source">Open source</option>
            </select>
          </label>
        </div>
        <div className="checkbox-row">
          <label><input type="checkbox" checked={form.featured} onChange={(e) => update('featured', e.target.checked)} /> Featured</label>
          <label><input type="checkbox" checked={form.verified} onChange={(e) => update('verified', e.target.checked)} /> Verified</label>
        </div>
        {msg && <div className="error-msg">{msg}</div>}
        <div className="form-actions">
          <Link to="/admin/tools" className="pill-btn">Cancel</Link>
          <button type="submit" className="pill-btn primary" disabled={saving} data-testid="admin-create-submit">
            {saving ? '…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
